import type { Locale } from '@rythmo/shared';

/**
 * The console's own text. The domain's words, the roles and every refusal come from
 * `@rythmo/messages` (ADR-0064); only what a console screen says lives here. Sentence case, second
 * person, verbs on buttons.
 */
export interface Words {
  console: string;
  starting: string;
  cannotStart: string;
  signInAgain: string;
  signOut: string;
  noAccess: { title: string; body: string };
  signedOut: { title: string; body: string };
  signInFailed: { title: string; body: string };
  picker: { title: string; body: string; notHere: string; tryAgain: string; loadFailed: string };
  shell: {
    hello: (name: string) => string;
    at: (school: string, role: string) => string;
    switchSchool: string;
    nav: string;
    sections: {
      today: string;
      schedule: string;
      catalog: string;
      plans: string;
      members: string;
      payments: string;
      staff: string;
      settings: string;
    };
    comingSoon: string;
    language: string;
    theme: string;
    themes: { system: string; light: string; dark: string };
    todaySessions: string;
    todaySessionsBody: string;
  };
}

const ro: Words = {
  console: 'Consola școlii',
  starting: 'Se deschide consola…',
  cannotStart: 'Consola nu poate porni acum. Reîncarcă pagina în câteva minute.',
  signInAgain: 'Intră din nou',
  signOut: 'Ieși din cont',
  noAccess: {
    title: 'Contul tău nu are acces la consolă',
    body: 'Consola este pentru proprietarii și managerii unei școli. Dacă ar trebui să ai acces, cere-i proprietarului școlii o invitație.',
  },
  signedOut: {
    title: 'Ai ieșit din cont',
    body: 'Sesiunea ta s-a încheiat. Intră din nou ca să continui.',
  },
  signInFailed: {
    title: 'Nu te-am putut conecta',
    body: 'Ceva n-a mers la conectare. Încearcă din nou.',
  },
  picker: {
    title: 'Alege școala',
    body: 'Lucrezi la mai multe școli. Alege-o pe cea în care intri acum.',
    notHere: 'Nu în consolă',
    tryAgain: 'Încearcă din nou',
    loadFailed: 'Nu ți-am putut încărca școlile.',
  },
  shell: {
    hello: (name) => `Bună, ${name}`,
    at: (school, role) => `${school} · ${role}`,
    switchSchool: 'Schimbă școala',
    nav: 'Secțiunile consolei',
    sections: {
      today: 'Azi',
      schedule: 'Program',
      catalog: 'Catalog',
      plans: 'Abonamente',
      members: 'Membri',
      payments: 'Plăți',
      staff: 'Echipă',
      settings: 'Setări',
    },
    comingSoon: 'Vine curând',
    language: 'Limba',
    theme: 'Tema',
    themes: { system: 'Ca sistemul', light: 'Luminoasă', dark: 'Întunecată' },
    todaySessions: 'Ședințele de azi',
    todaySessionsBody:
      'Aici vei vedea ședințele zilei, cu câte locuri mai sunt libere, odată ce programul școlii ajunge în consolă.',
  },
};

const en: Words = {
  console: 'School console',
  starting: 'Opening the console…',
  cannotStart: 'The console cannot start right now. Reload the page in a few minutes.',
  signInAgain: 'Sign in again',
  signOut: 'Sign out',
  noAccess: {
    title: "Your account can't use the console",
    body: "The console is for a school's owners and managers. If you should have access, ask the school's owner for an invitation.",
  },
  signedOut: {
    title: "You're signed out",
    body: 'Your session ended. Sign in again to carry on.',
  },
  signInFailed: {
    title: "We couldn't sign you in",
    body: 'Something went wrong while signing in. Try again.',
  },
  picker: {
    title: 'Choose your school',
    body: 'You work at more than one school. Choose the one to open now.',
    notHere: 'Not in the console',
    tryAgain: 'Try again',
    loadFailed: "We couldn't load your schools.",
  },
  shell: {
    hello: (name) => `Hello, ${name}`,
    at: (school, role) => `${school} · ${role}`,
    switchSchool: 'Switch school',
    nav: 'Console sections',
    sections: {
      today: 'Today',
      schedule: 'Schedule',
      catalog: 'Catalog',
      plans: 'Plans',
      members: 'Members',
      payments: 'Payments',
      staff: 'Staff',
      settings: 'Settings',
    },
    comingSoon: 'Coming soon',
    language: 'Language',
    theme: 'Theme',
    themes: { system: 'System', light: 'Light', dark: 'Dark' },
    todaySessions: "Today's sessions",
    todaySessionsBody:
      "This is where you'll see the day's sessions and how many places are left, once the school's schedule is in the console.",
  },
};

export const WORDS: Record<Locale, Words> = { ro, en };
